import { useEffect, useState, useCallback } from "react";
import { getStories, createStory, updateStory, deleteStory } from "./StoriesService";
import { useToast } from "../ToastContext";
import CommentThread from "./CommentThread";
import StoryDetail from "./StoryDetail";
import StoryFormModal from "./StoryFormModal";
import "./EpicsStories.css";

const STATUS_META = {
  PLANNING:  { label: "Planning",   cls: "es-status-planning"  },
  ACTIVE:    { label: "Active",     cls: "es-status-active"    },
  ON_HOLD:   { label: "On Hold",    cls: "es-status-onhold"    },
  COMPLETED: { label: "Completed",  cls: "es-status-completed" },
  ARCHIVED:  { label: "Archived",   cls: "es-status-archived"  },
};

const PRIORITY_META = {
  CRITICAL: { label: "Critical", color: "#ef4444" },
  HIGH:     { label: "High",     color: "#f97316" },
  MEDIUM:   { label: "Medium",   color: "#eab308" },
  LOW:      { label: "Low",      color: "#22c55e" },
  SOMEDAY:  { label: "Someday",  color: "#6b7280" },
};

export default function EpicDetail({ projectName, projectColor, epic, onBack, onEdit, onDelete }) {
  const toast = useToast();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [openStory, setOpenStory] = useState(null); // drilled-in story, or null = story list
  const [commentCount, setCommentCount] = useState(epic.commentCount || 0);

  const load = useCallback(() => {
    setLoading(true);
    getStories(epic.id)
      .then(setStories)
      .catch(() => toast("Failed to load stories", "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epic.id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editingStory) {
        const updated = await updateStory(epic.id, editingStory.id, data);
        setStories(ss => ss.map(s => s.id === updated.id ? updated : s));
        setOpenStory(o => (o && o.id === updated.id ? updated : o));
        toast("Story updated", "success");
      } else {
        const created = await createStory(epic.id, data);
        setStories(ss => [...ss, created]);
        toast("Story created", "success");
      }
      setShowForm(false);
      setEditingStory(null);
    } catch {
      toast(editingStory ? "Failed to update story" : "Failed to create story", "error");
    }
  };

  const handleDelete = async (story) => {
    if (!window.confirm(`Delete story "${story.name}"? Its tasks stay but lose their story link.`)) return;
    try {
      await deleteStory(epic.id, story.id);
      setStories(ss => ss.filter(s => s.id !== story.id));
      toast("Story deleted", "success");
    } catch {
      toast("Failed to delete story", "error");
    }
  };

  return (
    <>
      {openStory ? (
        <StoryDetail
          projectName={projectName}
          projectColor={projectColor}
          epicName={epic.name}
          story={openStory}
          onBack={() => { setOpenStory(null); load(); }}
          onEdit={() => { setEditingStory(openStory); setShowForm(true); }}
          onDelete={async () => { await handleDelete(openStory); setOpenStory(null); }}
        />
      ) : (
        <div className="es-epic-detail">
          <div className="es-breadcrumb">
            <button className="es-crumb-link" onClick={onBack}>🧩 Epics</button>
            <span className="es-crumb-sep">/</span>
            <span className="es-crumb-current">{epic.icon || "🧩"} {epic.name}</span>
          </div>

          <div className="es-detail-header">
            <div>
              <h3>{epic.name}</h3>
              {epic.description && <p className="es-detail-desc">{epic.description}</p>}
            </div>
            <div className="es-detail-actions">
              <button className="proj-btn-secondary" onClick={onEdit}>✏️ Edit</button>
              <button className="proj-btn-secondary es-btn-danger" onClick={onDelete}>🗑 Delete</button>
            </div>
          </div>

          <div className="es-section-header">
            <h4>Stories</h4>
            <button className="proj-btn-primary" onClick={() => { setEditingStory(null); setShowForm(true); }}>+ New Story</button>
          </div>

          {loading ? (
            <div className="es-empty">Loading stories…</div>
          ) : stories.length === 0 ? (
            <div className="es-empty">No stories yet. Break this epic into stories to organize tasks underneath.</div>
          ) : (
            <div className="es-story-list">
              {stories.map(s => (
                <div key={s.id} className="es-story-row" onClick={() => setOpenStory(s)}>
                  <div className="es-story-main">
                    <span className={`es-status-badge ${STATUS_META[s.status]?.cls || ""}`}>
                      {STATUS_META[s.status]?.label || s.status}
                    </span>
                    <span className="es-story-name">{s.name}</span>
                    {s.priority && (
                      <span className="es-story-priority" style={{ color: PRIORITY_META[s.priority]?.color }}>
                        {PRIORITY_META[s.priority]?.label}
                      </span>
                    )}
                  </div>
                  <div className="es-story-stats">
                    <span>✅ {s.doneTasks}/{s.totalTasks} tasks</span>
                    {s.commentCount > 0 && <span>💬 {s.commentCount}</span>}
                  </div>
                  <div className="es-story-actions">
                    <button className="es-icon-btn" onClick={ev => { ev.stopPropagation(); setEditingStory(s); setShowForm(true); }} title="Edit story">✏️</button>
                    <button className="es-icon-btn es-icon-btn-del" onClick={ev => { ev.stopPropagation(); handleDelete(s); }} title="Delete story">🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="es-section-header">
            <h4>Comments {commentCount > 0 && <span className="es-comment-count">({commentCount})</span>}</h4>
          </div>
          <CommentThread entityType="EPIC" entityId={epic.id} onCountChange={setCommentCount} />
        </div>
      )}

      {showForm && (
        <StoryFormModal
          story={editingStory}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingStory(null); }}
        />
      )}
    </>
  );
}
